import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Scale,
  Activity,
  Plus,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Minus,
  Droplet,
} from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { BackButton } from "@/components/ui/BackButton";
import { WeeklyCheckinModal } from "@/components/body/WeeklyCheckinModal";
import { getBodyHistory } from "@/api/body";
import { fmtDate } from "@/lib/dates";
import type {
  BodyAsymmetry,
  BodyCompositionLog,
  BodyCompositionTrendEntry,
} from "@/types";
import { cn } from "@/lib/utils";

const SEGMENT_LABELS: Record<string, string> = {
  torso: "torso",
  left_arm: "left_arm",
  right_arm: "right_arm",
  left_leg: "left_leg",
  right_leg: "right_leg",
};

export default function BodyComposition() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["body-history"],
    queryFn: () => getBodyHistory(180),
    staleTime: 10_000,
  });

  const latest = q.data?.latest;
  const logs = q.data?.logs ?? [];
  const trends = q.data?.trends ?? { insufficient_data: true };

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <BackButton to="/" label={t("body.back_home")} withHome={false} />

      <header className="flex items-start justify-between gap-3">
        <div>
          <SectionLabel>{t("body.title_section")}</SectionLabel>
          <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug md:text-[30px]">
            {t("body.title")}
          </h1>
          <p className="mt-1 text-[13px] text-taupe">
            {t("body.subtitle")}
            Withings).
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          {t("body.record")}
        </Button>
      </header>

      {q.isLoading ? (
        <Card>
          <p className="text-[13px] text-taupe">{t("body.loading")}</p>
        </Card>
      ) : !latest ? (
        <Card>
          <div className="py-6 text-center">
            <Scale className="mx-auto mb-2 h-8 w-8 text-taupe/60" strokeWidth={1.6} />
            <p className="text-[14px] font-medium text-navy-deep">
              {t("body.empty_title")}
            </p>
            <p className="mx-auto mt-1 max-w-md text-[13px] text-taupe">
              {t("body.empty_desc")}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <SnapshotCard latest={latest} />
          <TrendsCard trends={trends} logs={logs} />
          {(latest.segmental_fat || latest.segmental_muscle || latest.segmental_mq) ? (
            <SegmentalCard latest={latest} />
          ) : null}
          <HistoryCard logs={logs} />
        </>
      )}

      {open ? (
        <WeeklyCheckinModal
          onClose={() => setOpen(false)}
          onSaved={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function SnapshotCard({ latest }: { latest: BodyCompositionLog }) {
  const { t } = useTranslation();
  const measured = new Date(latest.measured_at);
  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <SectionLabel>{t("body.latest_measurement")}</SectionLabel>
        <span className="text-[11px] text-taupe">
          {measured.toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
            hour: "2-digit",
            minute: "2-digit",
          })}
          {" · "}
          {latest.source === "tanita_rd545" ? "Tanita RD-545" : latest.source}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label={t("body.weight_label")} value={`${latest.weight_kg}`} unit={t("body.kg")} highlight />
        {latest.bmi != null ? (
          <Metric label="BMI" value={`${latest.bmi}`} unit="" />
        ) : null}
        {latest.body_fat_pct != null ? (
          <Metric label={t("body.fat_label")} value={`${latest.body_fat_pct}`} unit="%" />
        ) : null}
        {latest.muscle_mass_kg != null ? (
          <Metric label={t("body.muscle_label")} value={`${latest.muscle_mass_kg}`} unit={t("body.kg")} />
        ) : null}
        {latest.visceral_fat_rating != null ? (
          <Metric
            label={t("body.visceral_label")}
            value={`${latest.visceral_fat_rating}`}
            unit=""
            tone={latest.visceral_fat_rating > 12 ? "warn" : undefined}
          />
        ) : null}
        {latest.body_water_pct != null ? (
          <Metric
            label={<WaterLabel />}
            value={`${latest.body_water_pct}`}
            unit="%"
          />
        ) : null}
        {latest.bmr_kcal != null ? (
          <Metric label="BMR" value={`${latest.bmr_kcal}`} unit={t("body.kcal")} />
        ) : null}
        {latest.metabolic_age != null ? (
          <Metric label="Meta age" value={`${latest.metabolic_age}`} unit={t("body.years")} />
        ) : null}
      </div>
    </Card>
  );
}

function WaterLabel() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1">
      <Droplet className="h-3 w-3" strokeWidth={1.8} />
      {t("body.water_label", { defaultValue: "Water" })}
    </span>
  );
}

function Metric({
  label,
  value,
  unit,
  highlight,
  tone,
}: {
  label: React.ReactNode;
  value: string;
  unit: string;
  highlight?: boolean;
  tone?: "warn";
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md bg-cream px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-taupe">
        {label}
      </div>
      <div
        className={cn(
          "metric-number mt-0.5",
          highlight ? "text-[22px]" : "text-[18px]",
          tone === "warn" ? "text-coral-dark" : "text-navy-deep",
        )}
      >
        {value}
        {unit ? <span className="ml-1 text-[11px] text-taupe">{unit}</span> : null}
      </div>
    </div>
  );
}

function TrendsCard({
  trends,
  logs,
}: {
  trends: import("@/types").BodyCompositionTrends;
  logs: BodyCompositionLog[];
}) {
  const { t } = useTranslation();
  if (trends.insufficient_data) {
    return (
      <Card>
        <SectionLabel>{t("body.trends")}</SectionLabel>
        <p className="mt-2 text-[13px] text-taupe">
          {t("body.need_2_measurements", { n: logs.length })}
        </p>
      </Card>
    );
  }

  const rows: Array<{ key: string; label: string; entry: BodyCompositionTrendEntry; unit: string }> = [];
  if (trends.weight) rows.push({ key: "weight", label: "Вес", entry: trends.weight, unit: "кг" });
  if (trends.body_fat) rows.push({ key: "body_fat", label: "% жира", entry: trends.body_fat, unit: "%" });
  if (trends.muscle_mass) rows.push({ key: "muscle_mass", label: "Мышцы", entry: trends.muscle_mass, unit: "кг" });
  if (trends.visceral_fat) rows.push({ key: "visceral_fat", label: "Висцер.", entry: trends.visceral_fat, unit: "" });

  return (
    <Card>
      <SectionLabel>Тренды · неделя и месяц</SectionLabel>
      <div className="mt-3 divide-y divide-cream-border">
        {rows.map((r) => (
          <TrendRow key={r.key} label={r.label} entry={r.entry} unit={r.unit} />
        ))}
      </div>

      {(trends.arm_fat_asymmetry?.alert ||
        trends.arm_mq_asymmetry?.alert ||
        trends.leg_fat_asymmetry?.alert) ? (
        <div className="mt-4 space-y-2">
          {trends.arm_fat_asymmetry?.alert ? (
            <AsymmetryAlert
              title="Асимметрия жира рук"
              asy={trends.arm_fat_asymmetry}
              unit="%"
            />
          ) : null}
          {trends.arm_mq_asymmetry?.alert ? (
            <AsymmetryAlert
              title="Асимметрия MQ рук"
              asy={trends.arm_mq_asymmetry}
              unit="MQ"
            />
          ) : null}
          {trends.leg_fat_asymmetry?.alert ? (
            <AsymmetryAlert
              title="Асимметрия жира ног"
              asy={trends.leg_fat_asymmetry}
              unit="%"
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function TrendRow({
  label,
  entry,
  unit,
}: {
  label: string;
  entry: BodyCompositionTrendEntry;
  unit: string;
}) {
  const { t } = useTranslation();
  const weekIcon = entry.week_delta < -0.1 ? TrendingDown : entry.week_delta > 0.1 ? TrendingUp : Minus;
  const weekCls = entry.week_delta < -0.1 ? "text-green-700" : entry.week_delta > 0.1 ? "text-coral-dark" : "text-taupe";
  const WeekIcon = weekIcon;
  return (
    <div className="flex items-center justify-between py-2 text-[13px]">
      <span className="font-medium text-navy-deep">{label}</span>
      <div className="flex items-center gap-3 text-taupe">
        <span>
          <span className="metric-number text-navy-deep">{entry.current}</span>
          {unit}
        </span>
        <span className={cn("inline-flex items-center gap-1", weekCls)}>
          <WeekIcon className="h-3 w-3" strokeWidth={2} />
          7д {entry.week_delta > 0 ? "+" : ""}{entry.week_delta}
        </span>
        {entry.month_delta != null ? (
          <span className="text-[12px]">
            30д {entry.month_delta > 0 ? "+" : ""}{entry.month_delta}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function AsymmetryAlert({
  title,
  asy,
  unit,
}: {
  title: string;
  asy: BodyAsymmetry;
  unit: string;
}) {
  const { t } = useTranslation();
  const delta = asy.difference_pct ?? asy.difference ?? 0;
  return (
    <div className="rounded-md border-l-4 border-coral/50 bg-coral/10 p-3 text-[12px]">
      <div className="flex items-center gap-1.5 font-medium text-coral-dark">
        <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
        {title} — разница {delta}{unit}
      </div>
      <div className="mt-1 text-taupe">
        Левая: <span className="metric-number text-navy-deep">{asy.left}</span>{unit}
        {" · "}
        Правая: <span className="metric-number text-navy-deep">{asy.right}</span>{unit}
      </div>
    </div>
  );
}

function SegmentalCard({ latest }: { latest: BodyCompositionLog }) {
  const { t } = useTranslation();
  return (
    <Card>
      <SectionLabel>{t("body.segmental_analysis")}</SectionLabel>
      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
        {latest.segmental_fat ? (
          <SegBlock title={t("body.fat_pct")} data={latest.segmental_fat} unit="%" />
        ) : null}
        {latest.segmental_muscle ? (
          <SegBlock title={t("body.muscle_kg")} data={latest.segmental_muscle} unit={t("body.kg")} />
        ) : null}
        {latest.segmental_mq ? (
          <SegBlock title={t("body.quality_mq")} data={latest.segmental_mq} unit="" />
        ) : null}
      </div>
    </Card>
  );
}

function SegBlock({
  title,
  data,
  unit,
}: {
  title: string;
  data: Record<string, number>;
  unit: string;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(data);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div>
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-amber-dark">
        {title}
      </div>
      <ul className="space-y-1.5">
        {entries.map(([key, value]) => (
          <li key={key} className="text-[12px]">
            <div className="flex justify-between">
              <span className="text-navy-deep">
                {t(`body.${key}`) ?? key}
              </span>
              <span className="metric-number text-taupe">
                {value}
                {unit}
              </span>
            </div>
            <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-cream-border">
              <div
                className="h-full rounded-full bg-amber"
                style={{ width: `${(value / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HistoryCard({ logs }: { logs: BodyCompositionLog[] }) {
  if (logs.length <= 1) return null;
  return (
    <Card>
      <SectionLabel>История</SectionLabel>
      <ul className="mt-3 divide-y divide-cream-border">
        {logs.slice(0, 15).map((l) => (
          <li key={l.id} className="flex justify-between py-2 text-[13px]">
            <span className="text-navy-deep">
              {fmtDate(l.measured_at, "d MMMM")}
            </span>
            <span className="flex items-center gap-3 text-taupe">
              <span className="metric-number">{l.weight_kg}</span> кг
              {l.body_fat_pct != null ? (
                <span>
                  <span className="metric-number">{l.body_fat_pct}</span>% жира
                </span>
              ) : null}
              <span className="text-[10px] uppercase tracking-wider">
                {l.source === "tanita_rd545" ? "Tanita" : l.source}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
